import type { FC } from 'react';
import { NavLink } from 'react-router-dom';
import useModules from '@utils/hooks/useModules';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useGetOrdersWithStatus } from '@pages/Orders/hooks/orders';
import { ORDER_STATUS_MAP } from '@utils/constants';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { useBlePrintingRollout } from '@hooks/useBlePrintingRollout';

const NavBarMobile: FC = () => {
  const { isFreemium } = useSubscription();
  const { hasModule } = useModules();
  const { orders } = useGetOrdersWithStatus();
  const pendingCount = orders.filter((o) => o.estatus === ORDER_STATUS_MAP.pending.key).length;
  // Punto "nueva versión" del rollout `menu-v2` en el item Menú (no-bloqueante).
  const { isEligibleInvitation: menuRolloutEligible } = useFeatureVersion(MENU_FEATURE_KEY);
  // Punto "Nuevo" del rollout `impresion-ble` en el item Ajustes: control invitable con
  // dispositivo compatible (el coachmark dentro de Ajustes → Impresión cierra la invitación).
  const { inviteEligible: bleRolloutEligible } = useBlePrintingRollout();

  const items = [
    {
      to: 'control-pedidos',
      label: 'Pedidos',
      show: isFreemium || hasModule(ModuleName.HISTORIAL),
      badge: pendingCount,
      dot: false,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
          <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      to: 'menu',
      label: 'Menú',
      show: isFreemium || hasModule(ModuleName.MENU),
      badge: 0,
      dot: menuRolloutEligible && Boolean(hasModule(ModuleName.MENU)),
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      to: 'estadisticas',
      label: 'Estadísticas',
      show: isFreemium || hasModule(ModuleName.ESTADISTICAS),
      badge: 0,
      dot: false,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      to: 'compartir-menu',
      label: 'Compartir',
      show: isFreemium || hasModule(ModuleName.COMPARTIR),
      badge: 0,
      dot: false,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
          <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
          <path d="M8.6 10.6l6.8-4.2M8.6 13.4l6.8 4.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      ),
    },
    {
      to: 'datos-del-comercio',
      label: 'Ajustes',
      show: true,
      badge: 0,
      dot: bleRolloutEligible,
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
          <path
            d="M19.4 15a1.7 1.7 0 0 0 .4 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.4 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .4-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.4-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.4H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.4l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.4 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"
            stroke="currentColor"
            strokeWidth="2"
          />
        </svg>
      ),
    },
  ].filter((it) => it.show);

  return (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 40,
        background: '#ffffff',
        borderTop: '1px solid #E5ECEE',
        display: 'flex',
        height: 60,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          style={({ isActive }) => ({
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            color: isActive ? '#4298B5' : '#6B6B6B',
            fontSize: 11,
            fontWeight: isActive ? 700 : 500,
            textDecoration: 'none',
            fontFamily: 'inherit',
            background: 'transparent',
            border: 0,
            cursor: 'pointer',
            position: 'relative',
          })}
        >
          {({ isActive }) => (
            <>
              <span style={{ position: 'relative' }}>
                <span style={{ display: 'inline-flex', color: isActive ? '#4298B5' : '#6B6B6B' }}>{it.icon}</span>
                {it.badge > 0 && (
                  <span
                    style={{
                      position: 'absolute',
                      top: -4,
                      right: -8,
                      background: '#FF3939',
                      color: '#ffffff',
                      fontSize: 10,
                      fontWeight: 700,
                      minWidth: 16,
                      height: 16,
                      padding: '0 4px',
                      borderRadius: 9999,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1.5px solid #ffffff',
                    }}
                  >
                    {it.badge > 99 ? '99+' : it.badge}
                  </span>
                )}
                {it.badge === 0 && it.dot && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      top: -3,
                      right: -5,
                      width: 8,
                      height: 8,
                      borderRadius: 9999,
                      background: '#1FC49A',
                      border: '1.5px solid #ffffff',
                    }}
                  />
                )}
              </span>
              <span>{it.label}</span>
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
};

export default NavBarMobile;
